/**
 * 관제 콘솔 셸 — 헤더 + 좌측 레일 + 스테이지 + (선택)우측 인스펙터.
 *
 * 기존 실시간 관제/ROI 편집은 Paper/Card 를 세로로 쌓아, 도면과 영상이 서로
 * 멀리 떨어지고 화면 높이를 못 쓰는 구조였다. 이 셸은 부모가 준 높이를 그대로
 * 채우고 각 pane 이 스스로 스크롤한다 — 페이지 전체 스크롤은 생기지 않는다.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Tooltip, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { consoleTokens } from '../../styles/consoleTokens';

export interface ConsoleShellProps {
  header: React.ReactNode;
  rail: React.ReactNode;
  children: React.ReactNode;
  inspector?: React.ReactNode;
  railOpen?: boolean;
  onToggleRail?: () => void;
  inspectorOpen?: boolean;
  onToggleInspector?: () => void;
  /** 초기 폭 — 이후에는 경계를 끌어 사용자가 조절한다. */
  railWidth?: number;
  inspectorWidth?: number;
}

const MIN_PANE = 180;
const MAX_PANE = 720;
/** 이보다 적게 움직였으면 드래그가 아니라 접기/펼치기 클릭으로 본다. */
const DRAG_SLOP = 4;

const ConsoleShell: React.FC<ConsoleShellProps> = ({
  header,
  rail,
  children,
  inspector,
  railOpen = true,
  onToggleRail,
  inspectorOpen = true,
  onToggleInspector,
  railWidth = 264,
  inspectorWidth = 260,
}) => {
  const t = consoleTokens(useTheme().palette.mode);
  const [railW, setRailW] = useState(railWidth);
  const [inspW, setInspW] = useState(inspectorWidth);
  const [dragging, setDragging] = useState(false);

  const pane = {
    bgcolor: t.panel,
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: 0,
    overflow: 'hidden',
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 520,
        bgcolor: t.bg,
        color: t.ink,
        border: `1px solid ${t.line}`,
        borderRadius: 1.5,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: '0 0 auto',
          bgcolor: t.panel,
          borderBottom: `1px solid ${t.line}`,
          px: 1.5,
          py: 1,
        }}
      >
        {header}
      </Box>

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* 좌측 레일 — 카메라 목록 */}
        <Box
          sx={{
            ...pane,
            width: railOpen ? railW : 0,
            flex: `0 0 ${railOpen ? railW : 0}px`,
            borderRight: railOpen ? `1px solid ${t.line}` : 'none',
            transition: dragging ? 'none' : 'width .18s ease, flex-basis .18s ease',
          }}
        >
          {railOpen && rail}
        </Box>

        {onToggleRail && (
          <RailHandle
            open={railOpen}
            onClick={onToggleRail}
            onResize={(w) => setRailW(w)}
            width={railW}
            side="left"
            onDraggingChange={setDragging}
          />
        )}

        {/* 스테이지 — 도면 + 영상 */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0, p: 1.25, gap: 1.25 }}>
          {children}
        </Box>

        {inspector && onToggleInspector && (
          <RailHandle
            open={inspectorOpen}
            onClick={onToggleInspector}
            onResize={(w) => setInspW(w)}
            width={inspW}
            side="right"
            onDraggingChange={setDragging}
          />
        )}

        {/* 우측 인스펙터 — 선택 대상 상세 */}
        {inspector && (
          <Box
            sx={{
              ...pane,
              width: inspectorOpen ? inspW : 0,
              flex: `0 0 ${inspectorOpen ? inspW : 0}px`,
              borderLeft: inspectorOpen ? `1px solid ${t.line}` : 'none',
              transition: dragging ? 'none' : 'width .18s ease, flex-basis .18s ease',
            }}
          >
            {inspectorOpen && inspector}
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * pane 경계 손잡이 — 끌면 폭 조절, 그냥 누르면 접기/펼치기.
 * (화면 한가운데 떠 있던 기존 토글 버튼을 대체한다.)
 */
const RailHandle: React.FC<{
  open: boolean;
  onClick: () => void;
  onResize: (width: number) => void;
  onDraggingChange: (dragging: boolean) => void;
  width: number;
  side: 'left' | 'right';
}> = ({ open, onClick, onResize, onDraggingChange, width, side }) => {
  const t = consoleTokens(useTheme().palette.mode);
  const drag = useRef<{ x: number; w: number; moved: boolean } | null>(null);

  const onMouseDown = (e: React.MouseEvent) => {
    if (!open) return; // 접힌 상태에서는 클릭(펼치기)만
    drag.current = { x: e.clientX, w: width, moved: false };
    e.preventDefault();
  };

  const finish = useCallback(() => {
    const d = drag.current;
    drag.current = null;
    onDraggingChange(false);
    if (d && !d.moved) onClick();
  }, [onClick, onDraggingChange]);

  useEffect(() => {
    const move = (e: MouseEvent) => {
      const d = drag.current;
      if (!d) return;
      const delta = side === 'left' ? e.clientX - d.x : d.x - e.clientX;
      if (!d.moved && Math.abs(delta) < DRAG_SLOP) return;
      if (!d.moved) { d.moved = true; onDraggingChange(true); }
      onResize(Math.min(MAX_PANE, Math.max(MIN_PANE, d.w + delta)));
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', finish);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', finish);
    };
  }, [side, onResize, finish, onDraggingChange]);

  const pointsLeft = side === 'left' ? open : !open;
  return (
    <Tooltip title={open ? '끌어서 폭 조절 · 클릭하면 접기' : '펼치기'} placement={side === 'left' ? 'right' : 'left'}>
      <Box
        onMouseDown={onMouseDown}
        onClick={() => { if (!open) onClick(); }}
        sx={{
          flex: '0 0 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: open ? 'col-resize' : 'pointer',
          color: t.faint,
          bgcolor: t.panelSoft,
          borderLeft: side === 'right' ? `1px solid ${t.line}` : 'none',
          borderRight: side === 'left' ? `1px solid ${t.line}` : 'none',
          '&:hover': { bgcolor: t.accentSoft, color: t.accent },
        }}
      >
        {pointsLeft ? <ChevronLeftIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
      </Box>
    </Tooltip>
  );
};

export default ConsoleShell;
